import React from "react";
import screenfull from "screenfull";
import Ticker from "react-ticker";

//Component Imports
import Image from "../Image";
import LanguagePicker from "../LanguagePicker";
import InGameHeaderFragment from "./InGameHeaderFragment";

//Module Imports
import moneyFormat from "@/modules/moneyFormat";
import { assetImg } from "@/modules/moduleImporter";
import { langLiteralType } from "@/modules/locales";
import { getBasePcode } from "@/modules/gamePcode";
import { defaultLogo, getWindowOpener, isDev } from "@/modules/commonMobile";

import {
	ILanguageType,
	IUserdataType,
	IGameObjectType,
	IKeyNumberDict,
	IKeyStringArrayDict
} from "@/types/GlobalTypes";

import "./index.scss";

/**
 * for use within this folder only
 */
export function allowFullscreen() {
	return !!getWindowOpener() || isDev();
}

/**
 * for use within this folder only
 */
export function toggleFullscreen() {
	if (screenfull.isEnabled)
		screenfull.toggle();
}

export interface IProps {
	games: IGameObjectType;
	userdata: IUserdataType;

	langdata: ILanguageType;
	activeGameCount: number;

	uniqueGamePcodes: string[];
	activeGamePcodes: string[];

	//Language Support
	langStr: langLiteralType;
	updateLanguage: (e: React.MouseEvent) => void;

	renderGame: (e: any) => void;

	closeAllGames: (e: any) => void;
	goToOperatorLobby: (e: any) => void;

	toggleHowToPlay: (e: any) => void;

	updateUserData: (e: any) => void;

	gameGroups: IKeyStringArrayDict;

	pcodeMax?: string;
	togglePopupTransactionHistory?: (e: any) => void;
	togglePopupResultHistory?: (e: any) => void;

	setAutoHideHeader: (value: boolean) => void;
}

interface IState {
	moveTicker: boolean,
	refreshBalance: boolean,
}

export default class DesktopHeader extends React.Component<IProps, IState> {
	state = {
		moveTicker: true,
		refreshBalance: false
	};

	private refreshBalance() {
		if (!this.state.refreshBalance)
			this.setState({ refreshBalance: true }, () => {
				window.setTimeout(() => this.setState({
					refreshBalance: false
				}), 2000)
			});

		this.props.updateUserData(null)
	}

	private startTicker() {
		this.setState({ moveTicker: true });
	}

	private stopTicker() {
		this.setState({ moveTicker: false });
	}

	private logoFragment() {
		const imgLogo = this.props.userdata.user.operatorLogo || defaultLogo;

		return (
			<Image className="img-logo"
				src={imgLogo}
				fallbackSrc={defaultLogo}
				alt="IDN Logo"
			/>
		)
	}

	private languagePickerFragment() {
		return (
			<LanguagePicker
				smallButton={true}
				{...this.props}
			/>
		);
	}

	private tickerFragment() {
		const { game } = this.props.userdata;

		return (
			<Ticker move={this.state.moveTicker}>
				{
					({ index }) => (
						game &&
						game.properties &&
						game.properties.map((item, idx) =>
							item.type === "running-text" &&
							<React.Fragment key={idx}>
								{item.content + "\u00a0\u00a0―\u00a0\u00a0"}
							</React.Fragment>
						)
					)
				}
			</Ticker>
		);
	}

	private runningTextFragment() {
		return (
			<div className="box-running-text-header">
				<div className="content-running-text-header">
					<div className="running-text-header"
						onMouseEnter={this.stopTicker.bind(this)}
						onMouseLeave={this.startTicker.bind(this)}>
						{this.tickerFragment()}
					</div>

					<div className="label-running-text-header">
						{this.props.langdata["txt_info"]} :
					</div>
				</div>
			</div>
		);
	}

	private nicknameFragment() {
		const { langdata, userdata } = this.props;

		return (
			<div className="box-header-desc">
				{assetImg("sidemenu", "user.svg", "", "sl-icon-header")}

				<div>
					<h4>
						{langdata["txt_userid"]}
					</h4>
					<p className="p-nickname">
						{userdata.user.displayNickname}
					</p>
				</div>
			</div>
		);
	}

	private userBalanceFragment() {
		const
			{ langdata, userdata } = this.props,
			{ refreshBalance } = this.state,
			{ balance, currency, lang } = userdata.user;

		return (
			<div className="box-header-desc">
				{assetImg("sidemenu", "balance.svg", "", "sl-icon-header")}

				<div>
					<h4>
						{langdata["txt_balance"]}
					</h4>

					<p className="balance">
						{moneyFormat(balance, lang, currency)}

						<button className={"button-refresh-balance " + (refreshBalance ? "spin" : "")}
							onClick={() => this.refreshBalance()}>
							{assetImg("sidemenu", "refresh.svg")}
						</button>
					</p>
				</div>
			</div>
		);
	}

	get lastGamePcode() {
		const { games } = this.props;
		const gamePcodes = Object.keys(games);

		return gamePcodes && gamePcodes.length > 0
			? getBasePcode(gamePcodes[gamePcodes.length - 1])
			: "";
	}

	private cornerFragment() {
		const
			{ lastGamePcode } = this,
			{ langdata, toggleHowToPlay } = this.props;

		return (
			<div className="box-button-header">
				{this.languagePickerFragment()}

				<button
					className="tip"
					{...{ game: lastGamePcode }}
					onClick={toggleHowToPlay}>

					{assetImg("sidemenu", "how_to_play.svg", "", "sl-icon-header")}

					<span>
						{langdata["txt_how_to_play"]}
					</span>
				</button>

				{
					allowFullscreen()
						? <button className="tip" onClick={toggleFullscreen.bind(this)}>
							{assetImg("sidemenu", "fullscreen.svg", "", "sl-icon-header")}

							<span>
								{langdata["txt_full_screen"]}
							</span>
						</button>
						: null
				}
			</div>
		);
	}

	private lobbyHeaderFragment() {
		return (
			<div className="sl-header-top-lobby">
				{this.logoFragment()}
				{this.runningTextFragment()}
				{this.nicknameFragment()}
				{this.userBalanceFragment()}
				{this.cornerFragment()}
			</div>
		);
	}

	render() {
		const { activeGameCount } = this.props;

		if (activeGameCount > 0)
			return <InGameHeaderFragment {...this.props} />;
		else return this.lobbyHeaderFragment();
	}
}